<template>
    <AppModal :title="$t('labels.product_keywords')" ref="Modal" size="lg" @on-hide="hide">
        <div class="modal-body">
            <template v-if="task.state.isRunning">
                <TaskProgressBar :done="task.state.done" :total="task.state.total"/>
            </template>
            <template v-else-if="Object.keys(keywords).length === 0">
                <FormGroup :label="$t('labels.language')">
                    <AppSelect
                        :model-value="selectedLanguage"
                        :selected-value="$t(`languages.${selectedLanguage}`)"
                        @update:model-value="selectedLanguage = $event.value"
                        :options="integrationLanguages.map(language => ({ value: language, label: $t(`languages.${language}`) }))"
                        optionKey="value"
                        optionValue="label"
                    />
                </FormGroup>
            </template>
            <template v-else>
                <div class="form-group form-group-feedback form-group-feedback-left">
                    <input type="text" class="form-control" :placeholder="translate('components.input.placeholder')" v-model="search">
                    <div class="form-control-feedback">
                        <i class="icon-search4"></i>
                    </div>
                </div>
                <div class="table-responsive">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th width="400px" @click="selectOrdering('keyWord')" :class="[
                                    orderColumn === 'keyWord' && orderDir === 'desc' && 'sorting_desc',
                                    orderColumn === 'keyWord' && orderDir === 'asc' && 'sorting_asc',
                                    orderColumn !== 'keyWord' && 'sorting'
                                ]">{{ $t('labels.keyword') }}</th>
                                <th width="150px" @click="selectOrdering('count')" :class="[
                                    orderColumn === 'count' && orderDir === 'desc' && 'sorting_desc',
                                    orderColumn === 'count' && orderDir === 'asc' && 'sorting_asc',
                                    orderColumn !== 'count' && 'sorting'
                                ]">{{ $t('labels.product_count') }} / {{ totalProductsCount }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="keyword in rows[page - 1]">
                                <td>{{ keyword.keyWord }}</td>
                                <td>
                                    {{ keyword.count }}
                                    <span class="text-muted">
                                        {{ `(${keyword.percent.toFixed(2) + '%'})` }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </div>
        <div :class="['modal-footer', 'd-flex', Object.keys(keywords).length === 0 ? 'justify-content-end' : 'justify-content-center']" v-if="!task.state.isRunning">
            <AppButton @click="fetchKeywords" v-if="Object.keys(keywords).length === 0" color="indigo">
                {{ $t('buttons.upload') }}
            </AppButton>
            <ul class="pagination pagination-separated align-self-center" v-else>
                <li :class="['page-item', page === 1 && 'disabled']">
                    <a href="#" class="page-link legitRipple" @click="page !== 1 && page--">← &nbsp; {{ $t('buttons.prev') }}</a>
                </li>
                <li :class="['page-item', pagination === page && 'active']" v-for="pagination in visiblePages">
                    <a href="#" class="page-link legitRipple" @click="page = pagination">{{ pagination }}</a>
                </li>
                <li :class="['page-item', (page === rows.length || rows.length === 0) && 'disabled']">
                    <a href="#" class="page-link legitRipple" @click="page !== rows.length && page++">{{ $t('buttons.next') }} &nbsp; →</a>
                </li>
            </ul>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import TaskProgressBar from '../../../../components/new/progress/TaskProgressBar.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import AppSelect from '../../../../components/new/form/select/AppSelect.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';

import { httpClient } from '../../../../services/HttpClient';
import { chunk } from '../../../../utils';
import { createProgressTask } from '../../../../services/Progress/ProgressTask';

export default {
    name: "ProductKeywordsModal",
    components: {
        AppModal, TaskProgressBar, FormGroup, AppSelect, AppButton
    },
    props: {
        category: {
            type: Object,
            required: true
        },
        integrationLanguages: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            task: createProgressTask(),
            keywords: {},
            totalProductsCount: 0,
            page: 1,
            search: '',
            orderColumn: "count",
            orderDir: "desc",
            selectedLanguage: this.integrationLanguages[0] || null
        }
    },
    methods: {
        async show() {
            this.$refs.Modal.show();
        },
        hide() {
            this.task.cancel();
            this.$refs.Modal.hide();
            this.keywords = {};
            this.page = 1;
            this.totalProductsCount = 0;
            this.search = '';
            this.orderColumn = "count";
            this.orderDir = "desc";
        },
        fetchKeywords() {
            return this.task.run(async (progress) => {
                const products = await this.fetchProducts(progress);

                if (progress.signal.aborted) {
                    return;
                }

                this.totalProductsCount = products.length;
                this.keywords = this.processProductsResponse(products);
            });
        },
        selectOrdering(column) {
            if (this.orderColumn === column) {
                if (this.orderDir === "asc") {
                    this.orderDir = "desc";
                } else if (this.orderDir === "desc") {
                    this.orderDir = "";
                    this.orderColumn = "";
                } else {
                    this.orderDir = "asc";
                }
            } else {
                this.orderColumn = column;
                this.orderDir = "asc";
            }
        },
        async fetchProducts({signal, setTotal, tick}) {
            let totalProductsCount = 0;
            let products = [];

            await httpClient.get('/integrations/products', {
                integration_uuids: [this.$route.params.integrationUuid],
                category_id: this.category.id,
                limit: 0
            }).then(response => {
                totalProductsCount = response.data.meta.total;
            });

            setTotal(Math.ceil(totalProductsCount / 10));

            for (let i = 0; i < totalProductsCount; i += 10) {
                if (signal.aborted) {
                    break;
                }

                await httpClient.get('/integrations/products', {
                    integration_uuids: [this.$route.params.integrationUuid],
                    category_id: this.category.id,
                    limit: 10,
                    page: Math.floor(i / 10) + 1
                }).then(response => {
                    products = products.concat(response.data.data);
                });

                tick();
            }

            return products;
        },
        processProductsResponse(data) {
            let processedKeywords = {};

            for (let product of data) {
                let productKeywords = product.seos[this.selectedLanguage];

                if (!productKeywords) {
                    continue;
                }

                for (let keyWord of productKeywords.split(', ').map(item => item.trim())) {
                    if (!processedKeywords[keyWord]) {
                        processedKeywords[keyWord] = {
                            count: 0,
                            percent: 0,
                            keyWord: keyWord
                        };
                    }

                    processedKeywords[keyWord].count++;
                    processedKeywords[keyWord].percent = (processedKeywords[keyWord].count / this.totalProductsCount) * 100;
                }
            }

            return Object.values(processedKeywords)
        }
    },
    computed: {
        rows() {
            let sortedArray = [];

            if (this.orderColumn) {
                sortedArray = Object.values(this.keywords).sort((a, b) => {
                    if (this.orderDir === 'asc') {
                        return a[this.orderColumn] > b[this.orderColumn] ? 1 : -1;
                    } else if (this.orderDir === 'desc') {
                        return a[this.orderColumn] < b[this.orderColumn] ? 1 : -1;
                    }
                    return 0;
                });
            } else {
                sortedArray = Object.values(this.keywords);
            }

            sortedArray = sortedArray.filter(keyword => keyword.keyWord.toLowerCase().includes(this.search.toLowerCase()));

            return chunk(sortedArray, 10);
        },
        visiblePages() {
            const maxVisible = 5;
            let start = Math.max(1, this.page - Math.floor(maxVisible / 2));
            let end = Math.min(this.rows.length, start + maxVisible - 1);

            if (end - start < maxVisible - 1) {
                start = Math.max(1, end - maxVisible + 1);
            }

            return Array.from({ length: end - start + 1 }, (_, i) => start + i);
        }
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
<style scoped>
table thead .sorting, table thead .sorting_asc, table thead .sorting_desc {
    position: relative;
}

table td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

table thead .sorting:before {
    content: "";
    margin-top: -.125rem;
    opacity: .5;
}

table thead .sorting_desc:before {
    content: "";
    opacity: 1;
    margin-top: -.375rem;
}

table thead .sorting:after {
    content: "";
    margin-top: -.625rem;
    opacity: .5;
}

table thead .sorting_asc:after {
    content: "";
    opacity: 1;
    margin-top: -.375rem;
}

table thead .sorting:before, table thead .sorting:after, table thead .sorting_asc:after, table thead .sorting_desc:before {
    font-family: icomoon;
    position: absolute;
    right: 1.25rem;
    top: 50%;
    font-size: .75rem;
    display: inline-block;
    line-height: 1
}
</style>